import { useTriageStatus } from "admin/hooks/useTriageStatus";
import {
  SEVERITY_CONFIG,
  TRIAGE_STATUS_OPTIONS,
  getAgentLabel,
  getTriageStatusConfig,
} from "admin/types/constants/diagnostics";
import type {
  DiagnosticListItem,
  DiagnosticTriageStatus,
  DimensionScore,
  RubricDimension,
} from "admin/types/interfaces/diagnostics";
import { formatDiagnosticDate } from "admin/utils/formatting";
import { scoreColor, scoreBg } from "admin/utils/scoring";
import { ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import { cn } from "~/lib/utils";
import { nextgenAdminTurnPath } from "~/pages/manifold-nextgen/paths";

interface DiagnosticDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: DiagnosticListItem[];
  selectedIndex: number;
  onNavigate: (newIndex: number) => void;
  /** Total results across all pages */
  total: number;
  /** Current page offset (0-based) */
  offset: number;
  rubricDimensions: RubricDimension[];
}

const ScoreBar = ({ score, label }: { score: number | null; label: string }) => {
  const isNA = score == null;
  const pct = isNA ? 100 : (score / 5) * 100;
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-muted-foreground w-[180px] truncate shrink-0" title={label}>
        {label}
      </span>
      <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full transition-all",
            isNA ? "bg-muted-foreground/30" : scoreBg(score)
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={cn("text-sm font-medium tabular-nums w-8 text-right", scoreColor(score))}>
        {isNA ? "N/A" : score}
      </span>
    </div>
  );
};

const DimensionScoresSection = ({
  scores,
  rubricDimensions,
}: {
  scores: DimensionScore[];
  rubricDimensions: RubricDimension[];
}) => {
  const dimNameMap = new Map(rubricDimensions.map((d) => [d.name, d.displayName]));

  // Sort by rubric order, with N/A (null-score) dimensions at the bottom
  const dimOrder = new Map(rubricDimensions.map((d) => [d.name, d.sortOrder]));
  const sorted = [...scores].sort((a, b) => {
    if (a.score == null && b.score != null) return 1;
    if (a.score != null && b.score == null) return -1;
    return (dimOrder.get(a.dimension) ?? Infinity) - (dimOrder.get(b.dimension) ?? Infinity);
  });

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold">Dimension Scores</h4>
      <div className="space-y-2">
        {sorted.map((ds) => (
          <ScoreBar
            key={ds.dimension}
            score={ds.score}
            label={dimNameMap.get(ds.dimension) ?? ds.dimension}
          />
        ))}
      </div>
    </div>
  );
};

export const DiagnosticDetailDrawer = ({
  open,
  onOpenChange,
  items,
  selectedIndex,
  onNavigate,
  total,
  offset,
  rubricDimensions,
}: DiagnosticDetailDrawerProps) => {
  const { updateTriageStatus, updatingIds } = useTriageStatus();

  const dimNameMap = new Map(rubricDimensions.map((d) => [d.name, d.displayName]));

  const item = items[selectedIndex];
  if (!item) return null;

  const globalIndex = offset + selectedIndex;
  const hasPrev = globalIndex > 0;
  const hasNext = globalIndex < total - 1;

  const handlePrev = () => {
    if (selectedIndex > 0) {
      onNavigate(selectedIndex - 1);
    } else if (hasPrev) {
      // Need to go to previous page — signal with -1
      onNavigate(-1);
    }
  };

  const handleNext = () => {
    if (selectedIndex < items.length - 1) {
      onNavigate(selectedIndex + 1);
    } else if (hasNext) {
      // Need to go to next page — signal with items.length
      onNavigate(items.length);
    }
  };

  const handleStatusChange = (newStatus: DiagnosticTriageStatus) => {
    void updateTriageStatus(item.id, newStatus);
  };

  const severityConfig = SEVERITY_CONFIG[item.severity];
  const statusConfig = getTriageStatusConfig(item.triageStatus);
  const dimensionScores = item.dimensionScores ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg w-full flex flex-col gap-0 p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b space-y-3">
          <div className="flex items-start justify-between gap-4 pr-8">
            <div className="min-w-0">
              <SheetTitle className="text-base">Turn Detail</SheetTitle>
              <SheetDescription
                className="font-mono text-xs mt-1 truncate"
                title={item.conversationId}
              >
                {item.conversationId}
              </SheetDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold",
                severityConfig.badgeClass
              )}
            >
              {severityConfig.label}
            </span>
            <span className={cn("text-lg font-bold tabular-nums", scoreColor(item.averageScore))}>
              {item.averageScore != null ? item.averageScore.toFixed(2) : "N/A"}
            </span>
            <span className="text-sm text-muted-foreground">/ 5</span>
            <Button variant="outline" size="sm" className="ml-auto gap-1.5" asChild>
              <a
                href={nextgenAdminTurnPath(item.conversationId, item.turnId)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="View this turn in the session, opens in a new tab"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                View Turn
              </a>
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Created</span>
              <p className="font-medium mt-0.5">{formatDiagnosticDate(item.createdAt)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Failure Layer</span>
              <p className="font-medium mt-0.5">{getAgentLabel(item.primaryFailureAgent)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Retries</span>
              <p className="font-medium mt-0.5 tabular-nums">{item.signals?.retryCount ?? 0}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Errors</span>
              <p className="font-medium mt-0.5 tabular-nums">{item.signals?.errorCount ?? 0}</p>
            </div>
            <div className="col-span-2">
              <span className="text-muted-foreground">Trace ID</span>
              <p className="font-mono text-xs mt-0.5 truncate" title={item.turnId}>
                {item.turnId}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold">Triage Status</h4>
            {item.triageStatus === "excluded" ? (
              <span
                className={cn(
                  "inline-flex items-center text-sm border rounded-md px-3 py-1.5",
                  statusConfig.badgeClass
                )}
              >
                {statusConfig.label}
              </span>
            ) : (
              <Select
                value={item.triageStatus}
                onValueChange={(v) => handleStatusChange(v as DiagnosticTriageStatus)}
                disabled={updatingIds.has(item.id)}
              >
                <SelectTrigger
                  className={cn("w-[180px] text-sm border", statusConfig.badgeClass)}
                  aria-label="Update triage status"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIAGE_STATUS_OPTIONS.filter((opt) => opt.value !== "excluded").map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {item.failureSummary && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Failure Summary</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">{item.failureSummary}</p>
            </div>
          )}

          {dimensionScores.length > 0 && (
            <DimensionScoresSection scores={dimensionScores} rubricDimensions={rubricDimensions} />
          )}

          {dimensionScores.some((ds) => ds.evidence) && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Score Evidence</h4>
              {dimensionScores
                .filter((ds) => ds.evidence)
                .map((ds) => {
                  const label = dimNameMap.get(ds.dimension) ?? ds.dimension;
                  return (
                    <div key={ds.dimension} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {label}
                        </Badge>
                        <span
                          className={cn("text-xs font-medium tabular-nums", scoreColor(ds.score))}
                        >
                          {ds.score != null ? `${ds.score}/5` : "N/A"}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed pl-1">
                        {ds.evidence}
                      </p>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t px-6 py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrev}
            disabled={!hasPrev}
            aria-label="Previous diagnostic"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Prev
          </Button>
          <span className="text-sm text-muted-foreground tabular-nums">
            {globalIndex + 1} of {total.toLocaleString()}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleNext}
            disabled={!hasNext}
            aria-label="Next diagnostic"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
